import { TpWebGL2DRenderDataColoredPoints, TpWebGLRenderDataLayer } from './interface-renderData';
import { TpWebGL2DRenderLayer, TpWebGl2DContext, WebGL2DRenderTypes } from './interfaces';
import { setWebGLZoomPan } from './utils';

export interface TpRenderColoredPoints {
  renderType: WebGL2DRenderTypes.COLORED_POINTS;
  ctx: TpWebGl2DContext;
  layerId: string;
  pointSizeFactor: number;
  pointCount: number;
  positionBuffer: any;
  colorBuffer: any;
}

function create(ctx: TpWebGl2DContext, dataInp: TpWebGLRenderDataLayer): TpRenderColoredPoints {
  const gl = ctx.gl;
  const data = dataInp as TpWebGL2DRenderDataColoredPoints;

  // Create a buffer for the vertices
  const positionBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, data.posits, gl.STATIC_DRAW);

  // Create a buffer for the colors
  const colorBuffer = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, data.colors, gl.STATIC_DRAW);

  const renderCtx: TpRenderColoredPoints = {
    renderType: WebGL2DRenderTypes.COLORED_POINTS,
    layerId: data.layerId,
    pointSizeFactor: data.pointSizeFactor,
    ctx,
    positionBuffer,
    colorBuffer,
    pointCount: data.posits.length / 2,
  };

  return renderCtx;
}

function updateData(rendererInp: TpWebGL2DRenderLayer, dataInp: TpWebGLRenderDataLayer) {
  const renderer = rendererInp as TpRenderColoredPoints;
  const gl = renderer.ctx.gl;
  const data = dataInp as TpWebGL2DRenderDataColoredPoints;

  // NOTE: as a future improvement, we only update the buffer if the floatarray was changed (simple reference check)

  gl.bindBuffer(gl.ARRAY_BUFFER, renderer.positionBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, data.posits, gl.STATIC_DRAW);

  gl.bindBuffer(gl.ARRAY_BUFFER, renderer.colorBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, data.colors, gl.STATIC_DRAW);

  renderer.pointCount = data.posits.length / 2;
  renderer.pointSizeFactor = data.pointSizeFactor;
}

function performRender(webGlCtx: TpWebGl2DContext, renderCtxInp: TpWebGL2DRenderLayer) {
  const renderCtx = renderCtxInp as TpRenderColoredPoints;
  const gl = webGlCtx.gl;

  const myProgram = webGlCtx.programContextColoredPoints;
  gl.useProgram(myProgram.program);

  setWebGLZoomPan(webGlCtx, myProgram);

  // Setting the overall point size
  const pointSize = renderCtx.pointSizeFactor * webGlCtx.zoomFactor ** 0.25;
  gl.uniform1f(myProgram.locPointSize!, pointSize * webGlCtx.pixelRatio);

  gl.enable(gl.BLEND);
  gl.blendEquation(gl.FUNC_ADD);
  //gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA); // most logical choice for premultipliedAlpha ?
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  // Vertices
  gl.enableVertexAttribArray(myProgram.locAttribPosition);
  gl.bindBuffer(gl.ARRAY_BUFFER, renderCtx.positionBuffer);
  gl.vertexAttribPointer(
    myProgram.locAttribPosition,
    2, // 2 components per iteration (because only two coordinates per vertex are provided)
    gl.FLOAT,
    false, // don't normalize the data
    0, // stride 0 = move forward size * sizeof(type) each iteration to get the next position
    0, // offset
  );

  // Colors
  gl.bindBuffer(gl.ARRAY_BUFFER, renderCtx.colorBuffer);
  gl.vertexAttribPointer(
    myProgram.locAttribColor,
    4, // numComponents
    gl.FLOAT,
    false, // no normalization
    0, // auto stride
    0, // offset
  );
  gl.enableVertexAttribArray(myProgram.locAttribColor);

  gl.drawArrays(
    gl.POINTS,
    0, // offset
    renderCtx.pointCount, //count
  );
}

export const webGl2DColoredPoints = {
  create,
  updateData,
  performRender,
};
